<template>
  <div>
    <div class="d-flex justify-content-end mb-1">
      <b-button variant="primary" @click="openCreateModal">
        <feather-icon icon="PlusIcon" class="mr-50" /> Novo marco
      </b-button>
    </div>

    <b-row v-if="!milestones.length">
      <b-col cols="12">
        <span class="text-muted">Nenhum marco cadastrado ainda.</span>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-for="milestone in milestones"
        :key="milestone.id"
        cols="12"
        md="6"
        lg="4"
        class="mb-1"
      >
        <b-card class="milestone-card" no-body>
          <b-card-body>
            <div class="d-flex justify-content-between align-items-start">
              <div class="d-flex align-items-center">
                <input
                  type="color"
                  class="color-swatch mr-1"
                  :value="milestone.color || '#7367f0'"
                  @change="updateColor(milestone, $event.target.value)"
                />
                <div>
                  <h5 class="mb-0">{{ milestone.title }}</h5>
                  <small class="text-muted" v-if="milestone.target_date">
                    Meta: {{ formatDate(milestone.target_date) }}
                  </small>
                </div>
              </div>
              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                @click="removeMilestone(milestone)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>

            <div class="mt-1">
              <div class="d-flex justify-content-between">
                <small>{{ progressLabel(milestone) }}</small>
                <small>{{ progressPercent(milestone) }}%</small>
              </div>
              <b-progress
                :value="progressPercent(milestone)"
                max="100"
                :style="{ backgroundColor: '#eee' }"
              >
                <b-progress-bar
                  :value="progressPercent(milestone)"
                  :style="{ backgroundColor: milestone.color || '#7367f0' }"
                />
              </b-progress>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Create Milestone Modal -->
    <b-modal
      id="milestone-create-modal"
      title="Novo marco"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="!canSubmit || saving"
      @ok.prevent="submitMilestone"
      centered
    >
      <b-form-group label="Título">
        <b-form-input v-model="form.title" placeholder="Ex.: Reserva de emergência completa" />
      </b-form-group>

      <b-form-group label="Data alvo">
        <b-form-input v-model="form.target_date" type="date" />
      </b-form-group>

      <b-form-group label="Cor">
        <input type="color" v-model="form.color" class="color-swatch" />
      </b-form-group>

      <hr />

      <b-form-group label="Tarefas avulsas existentes (opcional)">
        <b-form-checkbox
          v-for="task in unassignedTasks"
          :key="task.id"
          v-model="form.task_ids"
          :value="task.id"
        >
          {{ task.title }}
        </b-form-checkbox>
        <span v-if="!unassignedTasks.length" class="text-muted">
          Não há tarefas avulsas disponíveis.
        </span>
      </b-form-group>

      <b-form-group label="Novas tarefas (opcional)">
        <div
          v-for="(newTask, index) in form.tasks"
          :key="index"
          class="d-flex mb-1"
        >
          <b-form-input
            v-model="newTask.title"
            placeholder="Título da tarefa"
            class="mr-1"
          />
          <b-button variant="flat-danger" class="btn-icon" @click="removeNewTask(index)">
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
        <b-button variant="outline-primary" size="sm" @click="addNewTask">
          <feather-icon icon="PlusIcon" class="mr-50" /> Adicionar tarefa
        </b-button>
      </b-form-group>

      <small v-if="!canSubmit" class="text-danger">
        Selecione ao menos uma tarefa existente ou informe uma nova tarefa para criar o marco.
      </small>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BModal,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BProgress,
  BProgressBar,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import moment from "moment";
import * as types from "../store/types";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BProgress,
    BProgressBar,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      saving: false,
      form: this.blankForm(),
    };
  },
  computed: {
    ...mapGetters({
      milestones: types.MILESTONES,
      tasks: types.TASKS,
    }),
    unassignedTasks() {
      return this.tasks.filter((task) => !task.milestone_id);
    },
    canSubmit() {
      const hasExistingTasks = this.form.task_ids.length > 0;
      const hasNewTasks = this.form.tasks.some(
        (task) => task.title && task.title.trim().length > 0
      );
      return !!this.form.title && (hasExistingTasks || hasNewTasks);
    },
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    ...mapActions({
      getMilestones: types.GET_MILESTONES,
      getTasks: types.GET_TASKS,
      createMilestone: types.CREATE_MILESTONE,
      updateMilestone: types.UPDATE_MILESTONE,
      deleteMilestone: types.DELETE_MILESTONE,
    }),
    blankForm() {
      return {
        title: "",
        target_date: null,
        color: "#7367f0",
        task_ids: [],
        tasks: [],
      };
    },
    fetchAll() {
      this.getMilestones(this.token).catch(() => this.notifyError());
      this.getTasks(this.token).catch(() => this.notifyError());
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    progressPercent(milestone) {
      const total = milestone.tasks ? milestone.tasks.length : 0;
      if (!total) return 0;
      const completed = milestone.tasks.filter((t) => t.completed_at).length;
      return Math.round((completed / total) * 100);
    },
    progressLabel(milestone) {
      const total = milestone.tasks ? milestone.tasks.length : 0;
      const completed = milestone.tasks
        ? milestone.tasks.filter((t) => t.completed_at).length
        : 0;
      return `${completed}/${total} tarefas concluídas`;
    },
    openCreateModal() {
      this.form = this.blankForm();
      this.$bvModal.show("milestone-create-modal");
    },
    addNewTask() {
      this.form.tasks.push({ title: "" });
    },
    removeNewTask(index) {
      this.form.tasks.splice(index, 1);
    },
    submitMilestone(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.canSubmit) return;
      this.saving = true;

      const payload = {
        token: this.token,
        title: this.form.title,
        target_date: this.form.target_date || null,
        color: this.form.color,
        task_ids: this.form.task_ids,
        tasks: this.form.tasks
          .filter((task) => task.title && task.title.trim().length > 0)
          .map((task) => ({ title: task.title })),
      };

      this.createMilestone(payload)
        .then(() => {
          this.$bvModal.hide("milestone-create-modal");
          this.fetchAll();
          this.notify("Marco criado!", "O marco foi criado com sucesso.", "success");
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            "Não foi possível criar o marco. Verifique os dados e tente novamente.";
          this.notify("Oops!", message, "danger");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    updateColor(milestone, color) {
      this.updateMilestone({ token: this.token, id: milestone.id, color })
        .then(() => {
          this.fetchAll();
        })
        .catch(() => {
          this.notifyError();
        });
    },
    async removeMilestone(milestone) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Deseja realmente excluir o marco "${milestone.title}"?`,
        {
          title: "Excluir marco",
          okTitle: "Excluir",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        }
      );
      if (!confirmed) return;

      this.deleteMilestone({ token: this.token, id: milestone.id })
        .then(() => {
          this.fetchAll();
          this.notify("Marco excluído!", "O marco foi excluído com sucesso.", "success");
        })
        .catch((error) => {
          if (error?.response?.status === 422) {
            this.notify(
              "Não é possível excluir",
              "Este marco ainda possui tarefas vinculadas. Desvincule ou exclua as tarefas antes de remover o marco.",
              "warning"
            );
            return;
          }
          this.notifyError();
        });
    },
    notify(title, text, variant) {
      this.toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === "danger" || variant === "warning" ? "AlertTriangleIcon" : "CheckCircleIcon",
          variant,
        },
      });
    },
    notifyError() {
      this.notify(
        "Oops!",
        "Ocorreu um erro. Entre em contato com o setor de TI.",
        "danger"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.color-swatch {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.milestone-card {
  height: 100%;
}
</style>
